export const PRODUCT_CART_CONTEXT_KEY = 'agm-product-cart-context'

export const PRODUCT_CART_VEHICLE_KEY = 'agm-product-cart-vehicle'

export const PRODUCT_CART_CONTEXT_TTL_MS = 7 * 24 * 60 * 60 * 1000

export const PRODUCT_CART_CONTEXT_MAX_ENTRIES = 30

const safely = fn => {
  try {
    return fn()
  } catch {
    return null
  }
}

const isBrowser = () => typeof window !== 'undefined'

const isPositiveInteger = value => Number.isInteger(value) && value > 0

const isPresent = value => value !== undefined && value !== null

const hasNoDetail = value => !isPresent(value) || value === -1

const isFresh = (timestamp, now) =>
  Number.isFinite(timestamp) &&
  now - timestamp >= 0 &&
  now - timestamp <= PRODUCT_CART_CONTEXT_TTL_MS

export const normalizeCartContext = context => {
  if (!context || typeof context !== 'object') return null
  const { headlight_type_id, vehicle_id, headlight_vehicle_detail_id } = context
  if (!isPositiveInteger(vehicle_id)) return null
  if (!isPresent(headlight_type_id)) {
    return hasNoDetail(headlight_vehicle_detail_id) ? { vehicle_id } : null
  }
  if (!isPositiveInteger(headlight_type_id)) return null
  if (hasNoDetail(headlight_vehicle_detail_id)) {
    return { headlight_type_id, vehicle_id }
  }
  if (!isPositiveInteger(headlight_vehicle_detail_id)) return null
  return { headlight_type_id, vehicle_id, headlight_vehicle_detail_id }
}

const normalizeEntry = (entry, now) => {
  if (!entry || typeof entry !== 'object') return null
  const { product_id, saved_at } = entry
  if (!isPositiveInteger(product_id) || !isFresh(saved_at, now)) return null
  const context = normalizeCartContext(entry.context)
  return context ? { product_id, saved_at, context } : null
}

export const liveCartContextEntries = (entries, now) => {
  if (!Array.isArray(entries)) return []
  const seen = new Set()
  return entries
    .map(entry => normalizeEntry(entry, now))
    .filter(Boolean)
    .sort((a, b) => b.saved_at - a.saved_at)
    .filter(entry => !seen.has(entry.product_id) && seen.add(entry.product_id))
    .slice(0, PRODUCT_CART_CONTEXT_MAX_ENTRIES)
}

export const withCartContext = (entries, productId, context, now) => {
  const live = liveCartContextEntries(entries, now)
  const normalized = normalizeCartContext(context)
  if (!isPositiveInteger(productId) || !normalized) return live
  return liveCartContextEntries(
    [
      { product_id: productId, saved_at: now, context: normalized },
      ...live.filter(entry => entry.product_id !== productId),
    ],
    now
  )
}

export const withoutVehicleCartContexts = (entries, vehicleId, now) =>
  liveCartContextEntries(entries, now).filter(
    entry => entry.context.vehicle_id !== Number(vehicleId)
  )

export const cartContextFor = (entries, productId, now) => {
  const entry = liveCartContextEntries(entries, now).find(
    item => item.product_id === productId
  )
  return entry ? entry.context : null
}

export const liveVehicleId = (mark, now) => {
  if (!mark || typeof mark !== 'object') return null
  const { vehicle_id, seen_at } = mark
  return isPositiveInteger(vehicle_id) && isFresh(seen_at, now)
    ? vehicle_id
    : null
}

export const cartContextApplies = (
  context,
  { contextVehicleId, lastVehicleId, selectedVehicleId } = {}
) => {
  const normalized = normalizeCartContext(context)
  if (!normalized) return false
  const referenceVehicleId = [
    contextVehicleId,
    lastVehicleId,
    selectedVehicleId,
  ].find(isPresent)
  return (
    !isPresent(referenceVehicleId) ||
    Number(referenceVehicleId) === normalized.vehicle_id
  )
}

export const pickCartContext = ({
  tabContext,
  sharedContext,
  contextVehicleId,
  lastVehicleId,
  selectedVehicleId,
} = {}) => {
  if (cartContextApplies(tabContext, { contextVehicleId })) {
    return { context: normalizeCartContext(tabContext), pin: false }
  }
  if (
    cartContextApplies(sharedContext, {
      contextVehicleId,
      lastVehicleId,
      selectedVehicleId,
    })
  ) {
    return { context: normalizeCartContext(sharedContext), pin: true }
  }
  return { context: null, pin: false }
}

const sharedStorage = () => window.localStorage

const tabStorage = () => window.sessionStorage

const loadEntries = (storage, now) => {
  const raw = storage.getItem(PRODUCT_CART_CONTEXT_KEY)
  const parsed = raw ? safely(() => JSON.parse(raw)) : []
  return { raw, entries: liveCartContextEntries(parsed, now) }
}

const saveEntries = (storage, entries, raw) => {
  const serialized = entries.length ? JSON.stringify(entries) : null
  if (serialized === raw) return
  if (serialized) {
    storage.setItem(PRODUCT_CART_CONTEXT_KEY, serialized)
  } else {
    storage.removeItem(PRODUCT_CART_CONTEXT_KEY)
  }
}

const updateEntries = (storageOf, change, now) => {
  const loaded = safely(() => loadEntries(storageOf(), now))
  if (!loaded) return []
  const next = change(loaded.entries)
  safely(() => saveEntries(storageOf(), next, loaded.raw))
  return next
}

const readEntries = (storageOf, now) =>
  updateEntries(storageOf, entries => entries, now)

const readLastVehicleId = now =>
  safely(() => {
    const raw = sharedStorage().getItem(PRODUCT_CART_VEHICLE_KEY)
    const vehicleId = liveVehicleId(
      raw ? safely(() => JSON.parse(raw)) : null,
      now
    )
    if (raw && !vehicleId) sharedStorage().removeItem(PRODUCT_CART_VEHICLE_KEY)
    return vehicleId
  })

const writeLastVehicleId = (vehicleId, now) =>
  safely(() =>
    sharedStorage().setItem(
      PRODUCT_CART_VEHICLE_KEY,
      JSON.stringify({ vehicle_id: vehicleId, seen_at: now })
    )
  )

export const rememberVehiclePage = (vehicleId, now = Date.now()) => {
  if (!isBrowser() || !isPositiveInteger(vehicleId)) return
  writeLastVehicleId(vehicleId, now)
}

export const resolveProductCartContext = (
  productId,
  { contextVehicleId, selectedVehicleId } = {},
  now = Date.now()
) => {
  if (!isBrowser() || !isPositiveInteger(productId)) return null
  const { context, pin } = pickCartContext({
    tabContext: cartContextFor(readEntries(tabStorage, now), productId, now),
    sharedContext: cartContextFor(
      readEntries(sharedStorage, now),
      productId,
      now
    ),
    contextVehicleId,
    lastVehicleId: readLastVehicleId(now),
    selectedVehicleId,
  })
  if (pin) {
    updateEntries(
      tabStorage,
      entries => withCartContext(entries, productId, context, now),
      now
    )
  }
  return context
}

export const writeProductCartContext = (
  productId,
  context,
  now = Date.now()
) => {
  const normalized = normalizeCartContext(context)
  if (!isBrowser() || !isPositiveInteger(productId) || !normalized) return
  const remember = entries =>
    withCartContext(entries, productId, normalized, now)
  updateEntries(sharedStorage, remember, now)
  updateEntries(tabStorage, remember, now)
  writeLastVehicleId(normalized.vehicle_id, now)
}

export const forgetVehicleCartContexts = (vehicleId, now = Date.now()) => {
  if (!isBrowser() || !isPositiveInteger(Number(vehicleId))) return
  const forget = entries => withoutVehicleCartContexts(entries, vehicleId, now)
  updateEntries(sharedStorage, forget, now)
  updateEntries(tabStorage, forget, now)
}
